import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    EMPTY_ACTIVITY_FEED_FILTERS,
    activityFeedFiltersToApiParams,
    type ActivityFeedApiFilterParams,
    type ActivityFeedFiltersValue,
} from '../../components/activityFeedFilters';

export type InvoiceActivityLogEventType =
    | 'invoice-uploaded'
    | 'invoice-processed'
    | 'invoice-flagged'
    | 'invoice-modified'
    | 'invoice-submitted'
    | 'invoice-submit-failed'
    | 'invoice-archived'
    | 'invoice-unarchived'
    | 'invoices-bulk-archived'
    | 'invoices-bulk-unarchived'
    | 'invoice-pages-replaced'
    | 'invoice-settings-changed';

export interface InvoiceActivityLogChange {
    field: string;
    before?: string;
    after?: string;
}

export interface InvoiceActivityLogInvoiceRef {
    invoiceId: string;
    roNumber?: string;
    invoiceNumber?: string;
    supplierText?: string;
    invoiceType?: string;
}

export interface EnrichedInvoiceActivityLogEntry {
    _id: string;
    organizationId: string;
    invoiceId: string | null;
    invoiceUuid: string | null;
    userId: string | null;
    userName: { firstName: string; lastName: string } | null;
    timestamp: string;
    eventType: InvoiceActivityLogEventType;
    metadata?: Record<string, string>;
    invoiceRef?: InvoiceActivityLogInvoiceRef | null;
    /** Type in effect when this block was recorded. */
    invoiceTypeAtEvent?: string;
}

export interface EnrichedInvoiceActivityLogResponse {
    logs: EnrichedInvoiceActivityLogEntry[];
    totalCount: number;
}

export interface FetchInvoiceActivityLogsInput extends ActivityFeedApiFilterParams {
    invoiceId?: string;
    all?: boolean;
    page?: number;
    limit?: number;
    search?: string;
}

const getInvoiceActivityLogs = async (
    sessionId: string,
    input: FetchInvoiceActivityLogsInput,
): Promise<EnrichedInvoiceActivityLogResponse> => {
    const { data } = await axios.post(
        `${API_URL}/invoice/activity-logs`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useGetInvoiceActivityLogs = (
    invoiceId: string | undefined,
    page = 1,
    limit = 50,
    enabled = true,
    search = '',
    filters: ActivityFeedFiltersValue = EMPTY_ACTIVITY_FEED_FILTERS,
) => {
    const sessionContext = useContext(SessionContext);
    const trimmedSearch = search.trim();

    return useQuery({
        queryKey: ['/invoice/activity-logs', invoiceId, page, limit, trimmedSearch, filters],
        queryFn: () =>
            getInvoiceActivityLogs(sessionContext?.sessionId ?? '', {
                invoiceId: invoiceId!,
                page,
                limit,
                ...(trimmedSearch ? { search: trimmedSearch } : {}),
                ...activityFeedFiltersToApiParams(filters),
            }),
        enabled: !!sessionContext?.sessionId && !!invoiceId && enabled,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev, prevQuery) =>
            prevQuery?.queryKey[1] === invoiceId ? prev : undefined,
    });
};

export const useGetAllInvoiceActivityLogs = (
    page: number,
    limit = 50,
    enabled = true,
    search = '',
    filters: ActivityFeedFiltersValue = EMPTY_ACTIVITY_FEED_FILTERS,
) => {
    const sessionContext = useContext(SessionContext);
    const trimmedSearch = search.trim();

    return useQuery({
        queryKey: ['/invoice/activity-logs', 'all', page, limit, trimmedSearch, filters],
        queryFn: () =>
            getInvoiceActivityLogs(sessionContext?.sessionId ?? '', {
                all: true,
                page,
                limit,
                ...(trimmedSearch ? { search: trimmedSearch } : {}),
                ...activityFeedFiltersToApiParams(filters),
            }),
        enabled: !!sessionContext?.sessionId && enabled,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev, prevQuery) =>
            prevQuery?.queryKey[1] === 'all' ? prev : undefined,
    });
};

export const parseInvoiceActivityChanges = (
    metadata: Record<string, string> | undefined,
): InvoiceActivityLogChange[] => {
    if (!metadata?.changes) return [];
    try {
        const parsed = JSON.parse(metadata.changes);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};
